import React, {
	useCallback,
	useRef,
	useState,
	useEffect,
	forwardRef,
} from 'react';
import apiClient from '../../../../Services/ApiClient';
import TextField from '../TextField/TextField';
import { joinClasses } from '../../../../Utils/className.util';
import styles from './AddressWhisperer.module.scss';
import AddressSuggestionsDropdown from './AddressSuggestionsDropdown';

const MIN_SEARCH_LENGTH = 3;
const DEBOUNCE_MS = 300;

export interface AddressConfig {
	apiEndpoint?: string;
	queryParamName?: string;
	fieldMapping?: Record<string, string>;
	responseMapping?: Record<string, string>;
	onAddressSelect?: (item: Record<string, unknown>) => void;
}

interface AddressWhispererProps {
	addressConfig?: AddressConfig | null;
	value?: string;
	onChange?: (value: string) => void;
	placeholder?: string;
	label?: string;
	disabled?: boolean;
	wrapperClassName?: string;
	[name: string]: unknown;
	netteRules?: string;
	hasInvalid?: boolean;
}

const AddressWhisperer = forwardRef<HTMLInputElement, AddressWhispererProps>(
	(
		{
			addressConfig,
			value = '',
			onChange,
			placeholder,
			label,
			disabled,
			wrapperClassName,
			netteRules,
			hasInvalid = false,
			...textFieldProps
		},
		ref,
	) => {
		const [suggestions, setSuggestions] = useState<Record<string, unknown>[]>(
			[],
		);
		const [isLoading, setIsLoading] = useState(false);
		const [showDropdown, setShowDropdown] = useState(false);
		const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
		const containerRef = useRef<HTMLDivElement>(null);
		const suppressSearchRef = useRef(false);

		const hasAddressConfig = addressConfig && typeof addressConfig === 'object';
		const apiEndpoint = addressConfig?.apiEndpoint;
		const queryParam = addressConfig?.queryParamName ?? 'q';

		const searchAddresses = useCallback(
			async (searchText: string) => {
				if (
					!apiEndpoint ||
					!queryParam ||
					!searchText ||
					searchText.length < MIN_SEARCH_LENGTH
				) {
					setSuggestions([]);
					return;
				}
				setIsLoading(true);
				setShowDropdown(true);
				try {
					const url = `${apiEndpoint}?${queryParam}=${encodeURIComponent(
						searchText,
					)}`;
					const res = await apiClient.get(url);
					if (res?.data?.error || (res?.status ?? 0) >= 400) {
						setSuggestions([]);
					} else if (res?.data) {
						const data = Array.isArray(res.data)
							? res.data
							: res.data?.data ?? [];
						setSuggestions(data);
					} else {
						setSuggestions([]);
					}
				} catch {
					setSuggestions([]);
				} finally {
					setIsLoading(false);
				}
			},
			[apiEndpoint, queryParam],
		);

		const handleChange = useCallback(
			(val: string) => {
				onChange?.(val);
				if (!hasAddressConfig) return;
				if (suppressSearchRef.current) return;
				if (debounceRef.current) clearTimeout(debounceRef.current);
				if (!val || val.length < MIN_SEARCH_LENGTH) {
					setSuggestions([]);
					setShowDropdown(false);
					return;
				}
				debounceRef.current = setTimeout(
					() => searchAddresses(val),
					DEBOUNCE_MS,
				);
			},
			[hasAddressConfig, searchAddresses, onChange],
		);

		const handleSuggestionSelect = useCallback(
			(item: Record<string, unknown>) => {
				suppressSearchRef.current = true;
				if (debounceRef.current) clearTimeout(debounceRef.current);
				debounceRef.current = null;
				addressConfig?.onAddressSelect?.(item);
				setSuggestions([]);
				setShowDropdown(false);
				containerRef.current?.querySelector<HTMLInputElement>('input')?.blur();
				requestAnimationFrame(() => {
					suppressSearchRef.current = false;
				});
			},
			[addressConfig?.onAddressSelect],
		);

		const handleClickOutside = useCallback((e: MouseEvent) => {
			if (!containerRef.current?.contains(e.target as Node)) {
				setShowDropdown(false);
			}
		}, []);

		useEffect(() => {
			if (hasAddressConfig) {
				document.addEventListener('click', handleClickOutside, true);
			}
			return () => {
				document.removeEventListener('click', handleClickOutside, true);
				if (debounceRef.current) clearTimeout(debounceRef.current);
			};
		}, [hasAddressConfig, handleClickOutside]);

		if (!hasAddressConfig) {
			return (
				<TextField
					{...textFieldProps}
					value={value}
					onChange={onChange}
					placeholder={placeholder}
					label={label}
					disabled={disabled}
					wrapperClassName={wrapperClassName}
					ref={ref}
				/>
			);
		}

		return (
			<div
				ref={containerRef}
				className={joinClasses([wrapperClassName, styles.container])}
			>
				<TextField
					{...textFieldProps}
					value={value}
					onChange={handleChange}
					placeholder={placeholder}
					label={label}
					disabled={disabled}
					wrapperClassName={undefined}
					bounce={0}
					selfValue={false}
					autocomplete='off'
					ref={ref}
					className={joinClasses([
						styles.textField,
						hasInvalid && 'is-invalid',
						textFieldProps.className,
					])}
					firstUpper
					data-nette-rules={netteRules}
				/>
				{showDropdown && (
					<div className={styles.dropdown}>
						<AddressSuggestionsDropdown
							isLoading={isLoading}
							suggestions={suggestions}
							onSelect={handleSuggestionSelect}
						/>
					</div>
				)}
			</div>
		);
	},
);

AddressWhisperer.displayName = 'AddressWhisperer';

export default AddressWhisperer;
